<script setup lang="ts">
import { useEventListener, useResizeObserver } from '@vueuse/core';
import { nextTick, onMounted, ref, useTemplateRef } from 'vue';

defineOptions({ inheritAttrs: false });

const { scrollThreshold = 96 } = defineProps<{
	scrollThreshold?: number;
}>();

const viewportRef = useTemplateRef('viewport');
const contentRef = useTemplateRef('content');

const isPinnedToBottom = ref(true);
const isJumpingToBottom = ref(false);

function getDistanceFromBottom() {
	const viewport = viewportRef.value;
	if (!viewport) return 0;

	return viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
}

function updatePinnedState() {
	const isAtBottom = getDistanceFromBottom() <= scrollThreshold;
	if (isJumpingToBottom.value && !isAtBottom) return;

	isJumpingToBottom.value = false;
	isPinnedToBottom.value = isAtBottom;
}

function scrollToBottom(behavior: ScrollBehavior = 'auto') {
	const viewport = viewportRef.value;
	if (!viewport) return;

	viewport.scrollTo({
		behavior,
		top: viewport.scrollHeight,
	});
}

function syncScroll(force = false) {
	if (!force && !isPinnedToBottom.value) return;

	nextTick(() => {
		scrollToBottom();
		updatePinnedState();
	});
}

function jumpToBottom() {
	isJumpingToBottom.value = true;
	isPinnedToBottom.value = true;
	scrollToBottom('smooth');
}

useEventListener(viewportRef, 'scroll', updatePinnedState);
useResizeObserver(viewportRef, () => syncScroll());
useResizeObserver(contentRef, () => syncScroll());

onMounted(() => {
	syncScroll(true);
	nextTick(() => syncScroll(true));
});
</script>

<template>
	<div class="relative flex min-h-0 grow">
		<div
			ref="viewport"
			data-testid="ai-chat-scroll-viewport"
			class="min-h-0 grow overflow-x-hidden overflow-y-auto"
			v-bind="$attrs"
		>
			<div ref="content" class="flex min-h-full flex-col justify-end">
				<slot />
			</div>
		</div>

		<DButton
			v-if="!isPinnedToBottom"
			data-testid="ai-chat-jump-latest"
			class="absolute inset-x-0 bottom-4 z-10 mx-auto w-fit rounded-full shadow-lg"
			icon="lucide:arrow-down"
			:label="$t('ai.chat.jump_to_latest')"
			size="sm"
			color="neutral"
			variant="outline"
			@click="jumpToBottom"
		/>
	</div>
</template>
